/**
 * CostByTierTable - concrete "cost to build" table for AI Overview / AEO.
 *
 * Publishes real $/m2 AND total-build ranges by build tier so answer engines
 * (Google AI Mode / AI Overviews) and readers get a direct, structured,
 * number-led answer to "how much does it cost to build a house in X".
 * Includes a speakable quick-answer line, a dated disclaimer, external
 * references, and a link to the "what's not included" breakdown.
 *
 * Props:
 *   heading     string
 *   quickAnswer string   (concrete headline range; rendered speakable)
 *   basisLabel  string   (e.g. "standard 200 m² home, base build excl. land")
 *   tiers       Array<{ tier, sub?, perSqm, total }>
 *   footnote    string
 *   sources     Array<{ label, url }>
 *   notIncludedTo string? (internal link to the included-vs-extra / hidden-costs page)
 *   testId      string?
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

export const CostByTierTable = ({
  heading,
  quickAnswer,
  basisLabel,
  tiers = [],
  footnote = null,
  sources = [],
  notIncludedTo = null,
  testId = 'cost-by-tier',
}) => {
  if (tiers.length === 0) return null;

  return (
    <section data-testid={testId}>
      <h2 className="text-2xl sm:text-3xl font-bold text-[#1d1d1f] mb-3 tracking-tight">{heading}</h2>

      {quickAnswer && (
        <div className="rounded-xl border-l-4 border-[#1d8efe] bg-[#fafbfc] p-4 sm:p-5 mb-5" data-testid={`${testId}-quick-answer`}>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-[#1d8efe] mb-1">Quick answer</p>
          <p className="text-[15px] text-[#1d1d1f] leading-relaxed" data-speakable="true">{quickAnswer}</p>
        </div>
      )}

      <div className="overflow-x-auto border border-[#e8e8ed] rounded-xl" data-testid={`${testId}-table`}>
        <table className="w-full text-sm min-w-[520px]" data-speakable="true">
          <thead>
            <tr className="bg-[#f5f5f7] text-left">
              <th className="px-4 py-3 font-semibold text-[#1d1d1f]">Build tier</th>
              <th className="px-4 py-3 font-semibold text-[#1d1d1f]">Cost per m&sup2;</th>
              <th className="px-4 py-3 font-semibold text-[#1d1d1f]">Est. total build{basisLabel ? ` (${basisLabel})` : ''}</th>
            </tr>
          </thead>
          <tbody>
            {tiers.map((t, i) => (
              <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-[#fafafa]'} data-testid={`${testId}-row-${i + 1}`}>
                <td className="px-4 py-3 text-[#1d1d1f] font-medium">
                  {t.tier}
                  {t.sub && <><span className="sr-only"> &ndash; </span><span className="block text-[12px] text-[#86868b] font-normal">{t.sub}</span></>}
                </td>
                <td className="px-4 py-3 text-[#424245] whitespace-nowrap">{t.perSqm}</td>
                <td className="px-4 py-3 text-[#424245] whitespace-nowrap">{t.total}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {notIncludedTo && (
        <Link
          to={notIncludedTo}
          className="inline-flex items-center gap-1.5 text-[14px] font-medium text-[#1d8efe] mt-4 hover:underline"
          data-testid={`${testId}-not-included-link`}
        >
          What a quoted price does and doesn&apos;t include <ArrowRight className="w-4 h-4" />
        </Link>
      )}

      {footnote && <p className="text-[12px] text-[#86868b] leading-relaxed mt-4">{footnote}</p>}

      {sources.length > 0 && (
        <p className="text-[12px] text-[#86868b] leading-relaxed mt-2">
          <span className="font-semibold text-[#6e6e73]">Sources: </span>
          {sources.map((s, i) => (
            <React.Fragment key={i}>
              {i > 0 && '; '}
              {s.url ? (
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#1d8efe] hover:underline">{s.label}</a>
              ) : s.label}
            </React.Fragment>
          ))}
          .
        </p>
      )}
    </section>
  );
};

export default CostByTierTable;
